/**
 * Email sign-in, as plain data and pure functions.
 *
 * Everything here is free of fetch, storage and React so it can be tested
 * under node. `lib/auth.ts` does the requests; `components/EmailSignIn.tsx`
 * draws the steps; this file is what they both read.
 *
 * It is a port of the phone app's `src/auth/email.ts`, `emailErrors.ts` and
 * the masking in `src/accounts/hints.ts`, kept deliberately close to them: the
 * two clients talk to the same endpoints and a rule that differs between them
 * (what counts as an address, which failure is which) is one that only one of
 * them gets right.
 */
import { ApiError } from './api';

// ── the address ───────────────────────────────────────────────────────

/** The server's own limit on the field (`EmailStartSerializer`). */
export const EMAIL_MAX = 254;

const SHAPE = /^[^\s@]+@[^\s@.][^\s@]*\.[^\s@.]{2,}$/;

/** What was typed, cleaned up the way the screen sends it. */
export function cleanEmail(raw: string): string {
  return (raw ?? '').trim();
}

export type EmailProblem = 'empty' | 'invalid' | 'too_long';

/**
 * Why this is not an address yet, or null when it is plausible.
 *
 * The SERVER is the authority: it normalises (trim, lower-case, IDNA, and
 * Gmail's dots and +suffix) and answers the same way for a known and an
 * unknown address. This only stops what is plainly not one, so a code is not
 * sent to a typo that its owner then waits ten minutes for. Deliberately not
 * RFC 5322: a stricter rule here is a person with a perfectly good address
 * told it is wrong.
 */
export function emailProblem(raw: string): EmailProblem | null {
  const value = cleanEmail(raw);
  if (!value) return 'empty';
  if (value.length > EMAIL_MAX) return 'too_long';
  if (!SHAPE.test(value)) return 'invalid';
  const at = value.lastIndexOf('@');
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  // `..` and a leading or trailing dot are never valid in either half.
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return 'invalid';
  if (domain.endsWith('.') || domain.includes('..')) return 'invalid';
  return null;
}

const DOT = '•';

/**
 * `ada@gmail.com` -> `a••@gmail.com`. The domain stays visible, because
 * it is what says WHICH inbox the code went to; the local part is hidden
 * because a screen is a place other people can read.
 */
export function maskEmail(email: string): string {
  const raw = (email ?? '').trim();
  const at = raw.lastIndexOf('@');
  if (at < 1) return raw;
  const local = raw.slice(0, at);
  return `${local[0]}${DOT.repeat(Math.max(2, Math.min(local.length - 1, 4)))}${raw.slice(at)}`;
}

// ── what a failed start / verify means ───────────────────────────────

export type EmailFailure =
  /** 429 `resend_too_soon`: a code is already out for this address. Not an
   *  error to fix on the entry step - the code step is where it goes. */
  | { kind: 'resend_too_soon'; retryAfter: number; codeExpired: boolean }
  /** 429 with only a wait: a throttle (per address, per network). */
  | { kind: 'throttled'; retryAfter: number }
  /** 503 `email_unavailable`: the mail could not be sent right now. */
  | { kind: 'unavailable' }
  /** 404 / `email_login_disabled`: the switch is off (or an older server). */
  | { kind: 'off' }
  /** 403 `email_signup_disabled`: signing in works, creating an account does not. */
  | { kind: 'signup_closed' }
  /** 429 `signup_rate_limited`: too many accounts made from this network. */
  | { kind: 'signup_limited'; retryAfter: number }
  /** 403 `age_too_young` / `age_blocked`: a refusal that is final. */
  | { kind: 'age_refused' }
  /** 403 `app_check_failed`. */
  | { kind: 'app_check' }
  /** The request never got an answer. */
  | { kind: 'network' }
  /** 400 (wrong, expired or spent code) or any other answer with a sentence. */
  | { kind: 'detail'; detail: string; status: number }
  | { kind: 'other' };

function seconds(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : 0;
}

/** The body of an `ApiError`, as a record (it may be text, or absent). */
function bodyOf(err: ApiError): Record<string, unknown> {
  return err.body && typeof err.body === 'object' ? (err.body as Record<string, unknown>) : {};
}

/**
 * Read a failed email start / verify. The server answers with a `code` where
 * the screen has to do something different and a `detail` sentence elsewhere;
 * reading that once, here, is what lets the entry step and the code step agree
 * and lets the whole table be tested without a browser.
 */
export function readEmailFailure(error: unknown): EmailFailure {
  if (!(error instanceof ApiError)) {
    // `fetch` rejects with a TypeError when there is no connection, which is
    // the only thing it ever rejects with. An AbortError is a timeout.
    return error instanceof TypeError || (error as Error)?.name === 'AbortError'
      ? { kind: 'network' }
      : { kind: 'other' };
  }

  const status = error.status;
  const data = bodyOf(error);
  const code = typeof data.code === 'string' ? data.code : '';
  const detail = typeof data.detail === 'string' ? data.detail : '';

  if (code === 'email_login_disabled' || status === 404) return { kind: 'off' };
  if (code === 'email_signup_disabled') return { kind: 'signup_closed' };
  if (code === 'age_too_young' || code === 'age_blocked') return { kind: 'age_refused' };
  if (code === 'email_unavailable' || status === 503) return { kind: 'unavailable' };
  if (code === 'app_check_failed') return { kind: 'app_check' };
  if (code === 'signup_rate_limited') {
    return { kind: 'signup_limited', retryAfter: seconds(data.retry_after_seconds) };
  }
  if (status === 429) {
    if (code === 'resend_too_soon') {
      return {
        kind: 'resend_too_soon',
        retryAfter: seconds(data.retry_after_seconds),
        // The wait between codes can outlive a code: only an explicit zero
        // says the one already out has lapsed.
        codeExpired: data.code_valid_for_seconds === 0,
      };
    }
    return { kind: 'throttled', retryAfter: seconds(data.retry_after_seconds) };
  }
  if (detail) return { kind: 'detail', detail, status };
  return { kind: 'other' };
}

/**
 * A wait in seconds as the unit and count that say it, so the screen can
 * translate it: "Try again in 40s / 5 min / 2 h". Rounds UP - a button that
 * says "in 4 min" and then refuses at 3:59 is worse than one that says 5.
 */
export function waitParts(secondsLeft: number): { unit: 's' | 'min' | 'h'; n: number } {
  const s = Math.max(1, Math.ceil(secondsLeft));
  if (s >= 3600) return { unit: 'h', n: Math.ceil(s / 3600) };
  if (s >= 60) return { unit: 'min', n: Math.ceil(s / 60) };
  return { unit: 's', n: s };
}

// ── the registration-lock PIN ────────────────────────────────────────

/** The registration-lock PIN is six digits (`/v1/auth/lock/`). */
export const PIN_LENGTH = 6;

export function isValidPin(pin: string): boolean {
  return pin.length === PIN_LENGTH && /^\d+$/.test(pin);
}

export type PinFailure =
  /** 401: wrong PIN. `retryAfter` is the lock's own ladder, 0 = try again now. */
  | { kind: 'wrong'; retryAfter: number }
  /** 429: too many tries from here, or the account is cooling down. */
  | { kind: 'throttled'; retryAfter: number }
  /** 400: the challenge lapsed or was already spent. Start again. */
  | { kind: 'expired' }
  | { kind: 'app_check' }
  | { kind: 'network' }
  | { kind: 'other' };

/** Read a failed `POST /v1/auth/phone/lock/verify/`. */
export function readPinFailure(error: unknown): PinFailure {
  if (!(error instanceof ApiError)) {
    return error instanceof TypeError || (error as Error)?.name === 'AbortError'
      ? { kind: 'network' }
      : { kind: 'other' };
  }
  const data = bodyOf(error);
  const code = typeof data.code === 'string' ? data.code : '';
  if (code === 'app_check_failed') return { kind: 'app_check' };
  if (error.status === 401) return { kind: 'wrong', retryAfter: seconds(data.retry_after_seconds) };
  if (error.status === 429) {
    return { kind: 'throttled', retryAfter: seconds(data.retry_after_seconds) };
  }
  if (error.status === 400) return { kind: 'expired' };
  return { kind: 'other' };
}

// ── the date of birth ────────────────────────────────────────────────

/** The platform floor. A stricter country is enforced by the server, which
 *  knows the account's country; this only saves a round trip for the obvious. */
export const MINIMUM_AGE = 13;

/** Nobody alive is older than this; it catches a mistyped year. */
const MAX_AGE = 120;

/** Whole years on `today`, or null when the date will not parse. */
export function ageOn(iso: string, today: Date = new Date()): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const [, y, mo, d] = m;
  let years = today.getFullYear() - Number(y);
  const beforeBirthday =
    today.getMonth() + 1 < Number(mo) ||
    (today.getMonth() + 1 === Number(mo) && today.getDate() < Number(d));
  if (beforeBirthday) years -= 1;
  return years;
}

/** A real calendar date, not in the future and not absurdly old. */
export function isValidDob(iso: string, today: Date = new Date()): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return false;
  const [, y, mo, d] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  // Round-trip, so 31 February is rejected rather than rolled forward.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return false;
  }
  const age = ageOn(iso, today);
  return age !== null && age >= 0 && age <= MAX_AGE;
}

/**
 * `YYYY-MM-DD` from the three boxes, or null while they do not make a date.
 * The boxes start EMPTY and carry no hint of an acceptable answer: a default
 * is a suggestion about which birthday gets somebody through.
 */
export function dobFromParts(day: string, month: string, year: string): string | null {
  if (year.length !== 4 || !month || !day) return null;
  const iso = `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  return isValidDob(iso) ? iso : null;
}
